import React, { useEffect, useState } from 'react';
import { Modal } from 'reactstrap';
import Swal from "sweetalert2/dist/sweetalert2.js";
import { api, PHASES, fmtDate } from './api';
import { StatusBadge, OutcomeBadge } from './History';
import LogViewer from './LogViewer';
import PendingFields from './PendingFields';

const ALIVE = ["en_cola", "corriendo"];

// Barra de fases: hecha, en curso, fallida o por venir.
const PhaseBar = ({ execution }) => {
    const idx = PHASES.findIndex((p) => p.key === execution.phase);
    const finished = execution.status === "terminada";
    const failed = ["fallida", "colgada"].includes(execution.status);
    return (
        <div className="sd-phases">
            {PHASES.map((p, i) => {
                let cls = "";
                if (finished) cls = "is-done";
                else if (i < idx) cls = "is-done";
                else if (i === idx) cls = failed ? "is-failed" : "is-active";
                if (p.key === "archivos" && finished && execution.outcome !== "commiteada") cls = "";
                return (
                    <div key={p.key} className={`sd-phase ${cls}`}>
                        <i className={cls === "is-active" ? "fas fa-spinner fa-spin" : p.icon}></i> {p.label}
                    </div>
                );
            })}
        </div>
    );
};

const Stat = ({ value, label, tone }) => (
    <div className={`sd-stat ${tone ? `sd-stat--${tone}` : ""}`}>
        <div className="sd-stat__value">{value}</div>
        <div className="sd-stat__label">{label}</div>
    </div>
);

// Resumen legible de lo que dejó la corrida (`duplicator_executions.summary`).
const Summary = ({ execution }) => {
    const s = execution.summary || {};
    const copia = s.copia || {};
    const verif = s.verificacion || {};
    const salud = (s.salud && s.salud.conteos) || {};
    const archivos = s.archivos || {};
    const plan = s.plan || {};
    const verifTone = verif.ok === false ? "error" : verif.ok ? "ok" : null;
    return (
        <div className="sd-stats">
            <Stat value={copia.total != null ? copia.total : (plan.total != null ? plan.total : "—")} label={copia.total != null ? "elementos copiados" : "elementos en el plan"} />
            <Stat value={(copia.conteos && copia.conteos.formats) || (plan.conteos && plan.conteos.formats) || 0} label="formatos" />
            <Stat value={(copia.conteos && copia.conteos.questions) || (plan.conteos && plan.conteos.questions) || 0} label="preguntas" />
            {s.formatos_traidos > 0 && <Stat value={s.formatos_traidos} label="formatos externos traídos a la copia" />}
            {s.campos_por_llenar > 0 && <Stat tone="warn" value={s.campos_por_llenar} label="campos por llenar a mano en el destino" />}
            <Stat tone={verifTone} value={verif.ok === undefined ? "—" : verif.ok ? <i className="fas fa-check"></i> : (verif.conteos && verif.conteos.error)}
                label={verif.ok === undefined ? "verificación" : verif.ok ? "copia fiel (verificada)" : "diferencias del verificador"} />
            {s.salud && !s.salud.error && (
                <Stat tone={salud.nuevos ? "warn" : "ok"} value={salud.nuevos || 0}
                    label={`problemas nuevos de salud · ${salud.heredados || 0} ya venían del origen`} />
            )}
            {archivos.total > 0 && (
                <Stat tone={archivos.fallidos ? "warn" : "ok"} value={`${archivos.copiados}/${archivos.total}`} label="archivos copiados" />
            )}
            {s.log && <Stat tone={s.log.error ? "error" : s.log.aviso ? "warn" : null} value={s.log.aviso || 0} label={`avisos · ${s.log.error || 0} errores en el log`} />}
        </div>
    );
};

const RunView = ({ id, onOpen, onBack }) => {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [undoModal, setUndoModal] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = () => api.execution(id).then((r) => {
        if (!r.ok) return setError(r.error);
        setData(r);
    });

    const execution = data && data.execution;
    const live = !!execution && ALIVE.includes(execution.status);

    useEffect(() => { load(); }, [id]);
    useEffect(() => {
        if (!live) return undefined;
        const t = setInterval(load, 2000);
        return () => clearInterval(t);
    }, [id, live]);

    const repeat = (dryRun) => {
        const go = () => api.repeat(id, dryRun).then((r) => {
            if (!r.ok) return Swal.fire({ type: "error", title: "No se pudo lanzar", text: r.error });
            onOpen(r.execution.id);
        });
        if (dryRun) return go();
        Swal.fire({
            title: "¿Copiar de verdad?", text: "Misma selección y mismas decisiones. La copia queda en el destino y se puede deshacer.",
            type: "question", showCancelButton: true, confirmButtonText: "Sí, copiar", cancelButtonText: "Cancelar", confirmButtonColor: "#0061fe",
        }).then((res) => res.value && go());
    };

    // Deshacer corre en el WORKER (sus guardas tardan más de lo que aguanta una petición: en
    // producción daba 503). Se encola y se espera el resultado preguntando por la corrida, que es
    // donde el job lo deja (`summary.undo`).
    const esperarUndo = (modo, seco) => new Promise((resolve) => {
        const tic = setInterval(() => {
            api.execution(id).then((r) => {
                const u = r.ok && r.execution && r.execution.summary && r.execution.summary.undo;
                if (!u || u.en_curso) return;
                clearInterval(tic);
                resolve(u);
            });
        }, 2000);
    });

    // El ensayo se pide dos veces cuando hace falta: la primera sin forzar (que es lo que
    // contesta «esto no se puede quitar y por qué») y, si el operador quiere ver el forzado, otra
    // con `forzar` — que es la única que baja la lista de lo que se borraría del destino.
    const openUndo = (modo, forzar) => {
        setBusy(true);
        setUndoModal({ modo, forzar: !!forzar, preview: null, error: null, esperando: true, entiendo: false, numero: "" });
        api.undoPreview(id, modo, forzar).then((r) => {
            if (!r.ok) { setBusy(false); return setUndoModal({ modo, forzar: !!forzar, preview: null, error: r.error }); }
            esperarUndo(modo, true).then((u) => {
                setBusy(false);
                setUndoModal({ modo, forzar: !!forzar, entiendo: false, numero: "",
                    preview: u.ok ? { text: u.texto, report: u.reporte } : null,
                    error: u.ok ? null : (u.error || "No se pudo leer qué se quitaría") });
            });
        });
    };

    const doUndo = () => {
        setBusy(true);
        setUndoModal({ ...undoModal, esperando: true, error: null });
        api.undo(id, undoModal.modo, undoModal.forzar).then((r) => {
            if (!r.ok) { setBusy(false); return setUndoModal({ ...undoModal, error: r.error }); }
            esperarUndo(undoModal.modo, false).then((u) => {
                setBusy(false);
                if (!u.ok) return setUndoModal({ ...undoModal, esperando: false, error: u.error });
                setUndoModal(null);
                Swal.fire({ type: "success", title: undoModal.modo === "apagar" ? "Copia apagada" : "Copia deshecha",
                    text: u.texto || "Quedó registrado en el log de la corrida.", timer: 3000, showConfirmButton: false });
                load();
            });
        });
    };

    if (error) return <div className="rk-alert"><i className="fas fa-exclamation-circle"></i> {error}</div>;
    if (!execution) return <div className="rk-empty"><i className="fas fa-spinner fa-spin"></i><p>Cargando corrida…</p></div>;

    const manifest = data.manifest;
    // Lo que dejó dicho el ensayo: de aquí salen las dos puertas del forzado —si deshacer se va a
    // negar (hay referencias) y, con `forzar`, qué filas del destino se llevaría por delante.
    const reporte = (undoModal && undoModal.preview && undoModal.preview.report) || null;
    const referencias = (reporte && reporte.referencias) || [];
    const arrasado = (reporte && reporte.arrasado) || [];
    const bloqueada = referencias.length > 0;
    const puedeForzar = !!undoModal && undoModal.forzar && arrasado.length > 0 && !!undoModal.entiendo
        && String(undoModal.numero || "").trim() === String(execution.id);

    return (
        <div>
            <div className="sd-card">
                <div className="sd-footer" style={{ marginTop: 0, alignItems: "flex-start" }}>
                    <div>
                        <h3 className="sd-card__title">
                            <i className={execution.dry_run ? "fas fa-flask" : "fas fa-bolt"}></i>
                            Corrida #{execution.id} · {execution.dry_run ? "Ensayo" : "Copia real"}
                            <StatusBadge status={execution.status} /> <OutcomeBadge execution={execution} />
                        </h3>
                        <p className="sd-card__hint" style={{ marginBottom: 0 }}>
                            <b>{execution.source_name}</b> → <b>{execution.target_name}</b>
                            {" · "}lanzada {fmtDate(execution.created_at)}{execution.admin_email ? ` por ${execution.admin_email}` : ""}
                            {execution.duration ? ` · ${execution.duration}s` : ""}
                            {execution.attempts > 1 ? ` · ${execution.attempts} intentos` : ""}
                        </p>
                    </div>
                    <button type="button" className="rk-btn rk-btn--ghost" onClick={onBack}><i className="fas fa-arrow-left"></i> Historial</button>
                </div>
            </div>

            <PhaseBar execution={execution} />

            {execution.error_message && (
                <div className="rk-alert" style={{ marginBottom: 14, alignItems: "flex-start" }}>
                    <i className="fas fa-exclamation-circle" style={{ marginTop: 3 }}></i>
                    <div><b>{execution.status === "colgada" ? "La corrida se colgó" : "La corrida falló"}:</b> {execution.error_message}
                        <div style={{ fontWeight: 400, marginTop: 4 }}>Nada quedó a medias en el destino: la copia es una sola transacción.{execution.outcome === "commiteada" ? " (La copia sí alcanzó a guardarse: revisa el manifiesto.)" : ""}</div>
                    </div>
                </div>
            )}

            <Summary execution={execution} />

            {!live && (
                <div className="sd-card">
                    <h3 className="sd-card__title"><i className="fas fa-sliders-h"></i> Acciones</h3>
                    {execution.dry_run && execution.status === "terminada" && (
                        <p className="sd-card__hint"><i className="fas fa-check-circle" style={{ color: "#059669" }}></i> El ensayo terminó bien: la copia se hizo, se verificó y se revirtió. Si el resumen y el log te convencen, lánzala de verdad.</p>
                    )}
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                        {execution.dry_run && (
                            <button type="button" className="rk-btn rk-btn--primary" onClick={() => repeat(false)}><i className="fas fa-bolt"></i> Copiar de verdad</button>
                        )}
                        <button type="button" className="rk-btn rk-btn--soft" onClick={() => repeat(true)}><i className="fas fa-redo"></i> Repetir como ensayo</button>
                        {manifest && manifest.can_undo && (
                            <React.Fragment>
                                <button type="button" className="rk-btn rk-btn--ghost" disabled={busy} onClick={() => openUndo("quitar")}>
                                    <i className="fas fa-undo"></i> Deshacer la copia
                                </button>
                                <button type="button" className="rk-btn rk-btn--ghost" disabled={busy} onClick={() => openUndo("apagar")}>
                                    <i className="fas fa-eye-slash"></i> Solo apagarla
                                </button>
                            </React.Fragment>
                        )}
                    </div>
                    {manifest && !manifest.can_undo && manifest.undone_at && (
                        <p className="sd-muted" style={{ marginTop: 10, marginBottom: 0 }}><i className="fas fa-undo"></i> La copia se {manifest.state === "apagada" ? "apagó" : "deshizo"} el {fmtDate(manifest.undone_at)}.</p>
                    )}
                </div>
            )}

            {manifest && (
                <details className="sd-details">
                    <summary><i className="fas fa-clipboard-list" style={{ color: "#0061fe" }}></i> Manifiesto #{manifest.id}: {manifest.total} elementos en {manifest.tables.length} tablas ({manifest.state})</summary>
                    <div className="sd-details__body">
                        <div className="rk-chips">
                            {manifest.tables.map((t) => <span key={t.table} className="rk-chip">{t.table}: {t.rows}</span>)}
                        </div>
                        {manifest.files.length > 0 && (
                            <ul className="sd-list" style={{ marginTop: 10 }}>
                                {manifest.files.map((f, i) => (
                                    <li key={i} className={f.estado === "fallido" ? "is-error" : f.estado === "sin_origen" ? "is-warn" : "is-ok"}>
                                        <i className={f.estado === "fallido" ? "fas fa-times-circle" : "fas fa-file"}></i>
                                        <div>{f.table}.{f.column} «{f.valor}» <small>{f.estado || "pendiente"}{f.error ? ` — ${f.error}` : ""}</small></div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </details>
            )}

            {execution.outcome === "commiteada" && !live && (
                <PendingFields executionId={id} onLinked={load} />
            )}

            <div className="sd-card">
                <h3 className="sd-card__title"><i className="fas fa-terminal"></i> Log {live && <span className="sd-badge sd-badge--info"><i className="fas fa-circle"></i> en vivo</span>}</h3>
                <LogViewer id={id} live={live} />
            </div>

            <Modal className="modal-dialog-centered rk-modal modal-lg" isOpen={!!undoModal} toggle={() => !busy && setUndoModal(null)}>
                {undoModal && (
                    <React.Fragment>
                        <div className="rk-modal__header">
                            <span className="rk-modal__header-icon"><i className={undoModal.modo === "apagar" ? "fas fa-eye-slash" : "fas fa-undo"}></i></span>
                            <h5>{undoModal.modo === "apagar" ? "Apagar la copia" : "Deshacer la copia"}</h5>
                            <button type="button" className="rk-modal__close" onClick={() => !busy && setUndoModal(null)}><i className="fas fa-times"></i></button>
                        </div>
                        <div className="rk-modal__body">
                            <p className="sd-muted">
                                {undoModal.modo === "apagar"
                                    ? "Apagar no borra nada: los módulos y formatos copiados quedan invisibles, como cuando se eliminan desde la app."
                                    : undoModal.forzar
                                        ? "Deshacer FORZADO: además de quitar la copia, borra del destino lo que el cliente colgó encima de ella. Eso es trabajo suyo y no se puede devolver."
                                        : "Deshacer quita del destino todo lo que puso esta copia y lo deja como estaba. Si el cliente ya empezó a usar la copia (registros, preguntas nuevas), se niega y te dice qué lo impide; en ese caso, apágala."}
                            </p>
                            {undoModal.error && <div className="rk-alert" style={{ marginBottom: 10 }}><i className="fas fa-exclamation-circle"></i> {undoModal.error}</div>}
                            {undoModal.esperando && (
                                <p className="sd-muted"><i className="fas fa-spinner fa-spin"></i> Trabajando… Esto corre en segundo plano y puede tardar un par de minutos en una copia grande. Puedes dejar la ventana abierta.</p>
                            )}
                            {undoModal.preview && (
                                <React.Fragment>
                                    <label className="rk-label">Lo que se va a quitar (todavía no se ha borrado nada)</label>
                                    {/* Si el ensayo vuelve sin texto, se muestran los números del informe y, si
                                        tampoco están, se dice que no se pudo leer: un cuadro vacío no informa de
                                        nada y deja al usuario decidiendo a ciegas si borra o no. */}
                                    {undoModal.preview.text
                                        ? <div className="sd-pre">{undoModal.preview.text}</div>
                                        : undoModal.preview.report
                                            ? <div className="sd-pre">{JSON.stringify(undoModal.preview.report, null, 2)}</div>
                                            : <div className="rk-alert"><i className="fas fa-exclamation-circle"></i> No se pudo leer qué se quitaría. Revisa el log de la corrida (fase «deshacer») antes de continuar.</div>}
                                </React.Fragment>
                            )}
                            {/* La puerta al forzado solo aparece cuando el ensayo normal dice que
                                deshacer se va a negar: ofrecerla siempre sería invitar a borrarle
                                cosas al cliente sin necesidad. */}
                            {undoModal.modo === "quitar" && !undoModal.forzar && !undoModal.esperando && bloqueada && (
                                <div className="sd-danger" style={{ marginTop: 12 }}>
                                    <b><i className="fas fa-exclamation-triangle"></i> El destino colgó {referencias.length} cosa(s) encima de esta copia.</b>
                                    <p style={{ margin: "6px 0 10px" }}>
                                        Tal cual, deshacer se niega y lo único que queda es apagarla. Si aun así hay que quitarla,
                                        se puede <b>forzar</b>: se borra primero lo que el destino colgó encima. Eso es trabajo suyo y no vuelve.
                                    </p>
                                    <button type="button" className="rk-btn sd-btn-danger" disabled={busy} onClick={() => openUndo("quitar", true)}>
                                        <i className="fas fa-radiation"></i> Ver qué habría que borrar para forzarlo
                                    </button>
                                </div>
                            )}
                            {/* Doble confirmación: la casilla y el número de la corrida. Sin las dos,
                                el botón no se enciende — es lo único que separa un clic de borrarle
                                trabajo a un cliente. */}
                            {undoModal.forzar && !undoModal.esperando && undoModal.preview && (
                                arrasado.length === 0 ? (
                                    <div className="rk-alert" style={{ marginTop: 12, alignItems: "flex-start" }}>
                                        <i className="fas fa-ban" style={{ marginTop: 3 }}></i>
                                        <div>
                                            <b>Ni forzando.</b> Lo que cuelga de esta copia son <b>registros</b> del cliente
                                            (o su historial), y el forzado no se los lleva: una pregunta se rehace, un registro no.
                                            El final de esta corrida es apagarla.
                                        </div>
                                    </div>
                                ) : (
                                    <div className="sd-danger" style={{ marginTop: 12 }}>
                                        <b><i className="fas fa-radiation"></i> Se van a borrar {arrasado.length} cosa(s) del destino que NO son de la copia.</b>
                                        <ul className="sd-list" style={{ marginTop: 8 }}>
                                            {arrasado.map((a, i) => (
                                                <li key={i} className="is-error">
                                                    <i className="fas fa-times-circle"></i>
                                                    <div>
                                                        {a.nombre ? <b>{a.nombre}</b> : <b>{a.tabla}</b>}
                                                        {a.id ? ` · #${a.id}` : (a.cuantas > 1 ? ` · ${a.cuantas} filas` : "")}
                                                        <small>{[a.tabla, a.quien && `lo hizo ${a.quien}`, a.cuando].filter(Boolean).join(" · ")}</small>
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                        <label className="sd-radio-row" style={{ padding: "8px 0" }}>
                                            <input type="checkbox" checked={!!undoModal.entiendo}
                                                onChange={(e) => setUndoModal({ ...undoModal, entiendo: e.target.checked })} />
                                            Entiendo que esto borra trabajo del cliente que no es de la copia y que no se puede deshacer.
                                        </label>
                                        <div className="rk-field" style={{ maxWidth: 260 }}>
                                            <label className="rk-label">Escribe el número de la corrida ({execution.id}) para confirmar</label>
                                            <input className="rk-input" value={undoModal.numero || ""} placeholder={`${execution.id}`}
                                                onChange={(e) => setUndoModal({ ...undoModal, numero: e.target.value })} />
                                        </div>
                                    </div>
                                )
                            )}
                        </div>
                        <div className="rk-modal__footer">
                            <button type="button" className="rk-btn rk-btn--ghost" disabled={busy} onClick={() => setUndoModal(null)}>Cancelar</button>
                            {undoModal.forzar ? (
                                <button type="button" className="rk-btn sd-btn-danger" disabled={busy || !puedeForzar} onClick={doUndo}>
                                    {busy ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-radiation"></i>} Borrar eso y deshacer
                                </button>
                            ) : (
                                <button type="button" className="rk-btn rk-btn--primary" disabled={busy || !undoModal.preview} onClick={doUndo}>
                                    {busy ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-check"></i>} {undoModal.modo === "apagar" ? "Apagar" : "Deshacer"}
                                </button>
                            )}
                        </div>
                    </React.Fragment>
                )}
            </Modal>
        </div>
    );
};

export default RunView;
